import {
  ChevronRightRounded,
  EventAvailable,
  FactCheck,
  Flight,
  Home,
  SportsSoccer,
  Square,
} from '@mui/icons-material';
import { Box, Chip, CircularProgress, Grid, Tab, TableRow, Tabs, Tooltip } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Context } from '../../service/Context';
import { capitalizar, formatFechaPartido, ordenarLista, parseFechaTimestamp } from '../../utils/UtilesComun';
import {
  obtenerCategoriaCodigo,
  obtenerColorNota,
  obtenerEstadoJugadorEnCategoria,
  obtenerPorcentajeTitular,
} from '../../utils/UtilesJugador';
import {
  BadgeResultadoPartidoApp,
  ButtonTableApp,
  IconApp,
  PaperApp,
  TableCellApp,
  TableCellCategoria,
  TableCellTitular,
  TableModuleApp,
  TypographyApp,
  TypographyTableApp,
} from '../custom/CustomComponents';
import ModalHistorialRendimiento from '../jugador/ModalHistorialRendimiento';
import ModalValorarPartido from '../partidos/ModalValorarPartido';
import ClasificacionWrapper from './ClasificacionWrapper';

/**
 * Columnas de la tabla de partidos a evaluar (partidos disputados).
 */
const COLUMNAS_EVALUAR = [
  { field: 'fecha', label: '', width: '17%', sortable: false },
  { field: 'localia', label: '', width: '4.5%', sortable: false },
  { field: 'jornada', label: '', width: '5.5%', sortable: false },
  { field: 'resultado', label: '', width: '12%', sortable: false },
  { field: 'rival', label: '', width: '42.5%', sortable: false },
  { field: 'evaluar', label: '', width: '18.5%', sortable: false },
];

/**
 * Columnas de la tabla de próximos partidos (partidos futuros por disputar).
 * Nombre de equipo más amplio, fecha y hora más compactas y botón valorar activo.
 */
const COLUMNAS_PROXIMOS = [
  { field: 'fecha', label: '', width: '14.5%', sortable: false },
  { field: 'hora', label: '', width: '7%', sortable: false },
  { field: 'localia', label: '', width: '5.5%', sortable: false },
  { field: 'jornada', label: '', width: '5.5%', sortable: false },
  { field: 'rival', label: '', width: '49.5%', sortable: false },
  { field: 'evaluar', label: '', width: '18%', sortable: false },
];

/**
 * Columnas de la tabla de últimos partidos para equipos ajenos (sin opción de evaluar).
 */
const COLUMNAS_ULTIMOS_AJENO = [
  { field: 'fecha', label: '', width: '18%', sortable: false },
  { field: 'localia', label: '', width: '5.5%', sortable: false },
  { field: 'jornada', label: '', width: '6.5%', sortable: false },
  { field: 'resultado', label: '', width: '13%', sortable: false },
  { field: 'rival', label: '', width: '57%', sortable: false },
];

/**
 * Columnas de la tabla de próximos partidos para equipos ajenos (sin opción de evaluar).
 */
const COLUMNAS_PROXIMOS_AJENO = [
  { field: 'fecha', label: '', width: '17%', sortable: false },
  { field: 'hora', label: '', width: '9%', sortable: false },
  { field: 'localia', label: '', width: '6%', sortable: false },
  { field: 'jornada', label: '', width: '6.5%', sortable: false },
  { field: 'rival', label: '', width: '61.5%', sortable: false },
];

/**
 * Columnas para la tabla de rendimiento de plantilla de scouting.
 * Nombre amplio, Nota Media destacada con indicador interactivo y Categoría compacta.
 */
const COLUMNAS_RENDIMIENTO = [
  { field: 'nombreJugador', label: 'Nombre', width: '32%', tooltip: 'Nombre' },
  {
    field: 'notaMediaTemporada',
    label: 'Nota ›',
    width: '10.5%',
    tooltip: 'Nota Media (haz clic para ver historial de partidos y notas)',
  },
  { field: 'categoria', label: 'Cat', width: '7%', tooltip: 'Categoría', align: 'center' },
  { field: 'convocatorias', label: 'PC', width: '7%', tooltip: 'Partidos Convocado' },
  { field: 'titularidades', label: 'PT', width: '7%', tooltip: 'Titularidades' },
  { field: 'porcentajeTitular', label: '%T', width: '7.5%', tooltip: 'Porcentaje de titularidades' },
  { field: 'minutos', label: 'Min', width: '7.5%', tooltip: 'Minutos' },
  { field: 'goles', label: 'Goles', width: '7.5%', tooltip: 'Goles', icon: SportsSoccer },
  { field: 'amarillas', label: 'TA', width: '7%', tooltip: 'Tarjetas Amarillas', icon: Square, color: 'yellow' },
  { field: 'rojas', label: 'TR', width: '7%', tooltip: 'Tarjetas Rojas', icon: Square, color: 'red' },
];

/**
 * Renderiza el chip/botón del estado de evaluación de un partido.
 * "Valorar" para pendiente, "Continuar" para parcial, "Evaluado" para completado.
 */
const ChipEstadoEvaluacion = ({ estado, onClick }) => {
  let label = 'Valorar';
  let customStyle = {
    backgroundColor: '#f1f5f9',
    color: '#334155',
    border: '1px solid #cbd5e1',
  };

  if (estado === 'EVALUADO') {
    label = 'Evaluado';
    customStyle = {
      backgroundColor: '#ecfdf5',
      color: '#065f46',
      border: '1px solid #a7f3d0',
    };
  } else if (estado === 'PARCIAL') {
    label = 'Continuar';
    customStyle = {
      backgroundColor: '#fffbeb',
      color: '#92400e',
      border: '1px solid #fde68a',
    };
  }

  return (
    <Tooltip title="Valorar partido" arrow>
      <Chip
        size="small"
        label={label}
        onClick={onClick}
        clickable
        sx={{
          fontWeight: 600,
          fontSize: '0.74rem',
          height: 25,
          width: 80,
          minWidth: 80,
          maxWidth: 80,
          justifyContent: 'center',
          borderRadius: '5px',
          cursor: 'pointer',
          transition: 'all 0.15s ease-in-out',
          '&:hover': {
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.14)',
            filter: 'brightness(0.96)',
            transform: 'translateY(-0.5px)',
          },
          ...customStyle,
        }}
      />
    </Tooltip>
  );
};

ChipEstadoEvaluacion.propTypes = {
  estado: PropTypes.string,
  onClick: PropTypes.func,
};

/**
 * Fila compacta e individual para la tabla de evaluación de partidos.
 */
const FilaEvaluacionPartido = ({ partido, columns, onEvaluar }) => {
  const navigate = useNavigate();

  const idRival = partido.esLocal ? partido.idVisitante : partido.idLocal;
  const nombreRival = partido.rival || (partido.esLocal ? partido.nombreVisitante : partido.nombreLocal);
  const infoFecha = formatFechaPartido(partido.fecha);
  const textoFecha =
    infoFecha.diaSem && infoFecha.fecha && infoFecha.fecha !== '-' ? `${infoFecha.diaSem} ${infoFecha.fecha}` : '-';

  return (
    <TableRow key={partido.idPartido} hover>
      {columns.map((col) => {
        if (col.field === 'fecha') {
          return (
            <TableCellApp key={col.field} sx={{ py: 0.5, pl: 1.75, pr: 0.5, textAlign: 'left', width: col.width }}>
              {textoFecha !== '-' ? (
                <Tooltip title={infoFecha.completa || textoFecha} arrow>
                  <TypographyApp
                    sx={{
                      fontSize: '0.74rem',
                      color: 'text.secondary',
                      fontWeight: 600,
                      textAlign: 'left',
                      whiteSpace: 'nowrap',
                      width: '100%',
                    }}
                  >
                    {textoFecha}
                  </TypographyApp>
                </Tooltip>
              ) : (
                <TypographyApp
                  sx={{
                    fontSize: '0.74rem',
                    color: 'text.disabled',
                    textAlign: 'left',
                    whiteSpace: 'nowrap',
                    width: '100%',
                  }}
                >
                  -
                </TypographyApp>
              )}
            </TableCellApp>
          );
        }

        if (col.field === 'localia') {
          return (
            <TableCellApp align="center" key={col.field} sx={{ py: 0.5, px: 0, textAlign: 'center', width: col.width }}>
              <IconApp sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {partido.esLocal ? (
                  <Tooltip title="Local" arrow>
                    <Home sx={{ fontSize: '1.05rem', color: 'text.secondary' }} />
                  </Tooltip>
                ) : (
                  <Tooltip title="Visitante" arrow>
                    <Flight sx={{ fontSize: '1.05rem', color: 'text.secondary' }} />
                  </Tooltip>
                )}
              </IconApp>
            </TableCellApp>
          );
        }

        if (col.field === 'jornada') {
          return (
            <TableCellApp key={col.field} sx={{ py: 0.5, pl: 0.25, pr: 0, width: col.width }}>
              <TypographyApp sx={{ fontWeight: 700, fontSize: '0.78rem' }}>J{partido.jornada || '-'}</TypographyApp>
            </TableCellApp>
          );
        }

        if (col.field === 'resultado') {
          return (
            <TableCellApp key={col.field} sx={{ py: 0.5, pl: 0.25, pr: 0, textAlign: 'center', width: col.width }}>
              <BadgeResultadoPartidoApp partido={partido} onClick={() => navigate(`/partido/${partido.idPartido}`)} />
            </TableCellApp>
          );
        }

        if (col.field === 'rival') {
          return (
            <TableCellApp
              key={col.field}
              sx={{ py: 0.5, pl: 0, pr: onEvaluar ? 0.5 : 1.25, overflow: 'hidden', width: col.width }}
            >
              <ButtonTableApp
                onClick={() => {
                  if (idRival) {
                    navigate(`/equipo/${idRival}`);
                  }
                }}
                text={capitalizar(nombreRival)}
                tooltip={capitalizar(nombreRival)}
                sx={{ px: 0.25 }}
              />
            </TableCellApp>
          );
        }

        if (col.field === 'evaluar') {
          return (
            <TableCellApp key={col.field} sx={{ py: 0.5, pl: 0.25, pr: 1.25, textAlign: 'center', width: col.width }}>
              <ChipEstadoEvaluacion estado={partido.estadoEvaluacion} onClick={() => onEvaluar && onEvaluar(partido)} />
            </TableCellApp>
          );
        }

        return <TypographyTableApp key={col.field}>-</TypographyTableApp>;
      })}
    </TableRow>
  );
};

FilaEvaluacionPartido.propTypes = {
  partido: PropTypes.object.isRequired,
  columns: PropTypes.array.isRequired,
  onEvaluar: PropTypes.func,
};

/**
 * Fila compacta e individual para la tabla de próximos partidos (futuros por disputar).
 */
const FilaProximoPartido = ({ partido, columns, onEvaluar }) => {
  const navigate = useNavigate();

  const idRival = partido.esLocal ? partido.idVisitante : partido.idLocal;
  const nombreRival = partido.rival || (partido.esLocal ? partido.nombreVisitante : partido.nombreLocal);
  const infoFecha = formatFechaPartido(partido.fecha);
  const textoFecha =
    infoFecha.diaSem && infoFecha.fecha && infoFecha.fecha !== '-' ? `${infoFecha.diaSem} ${infoFecha.fecha}` : '-';
  const textoHora = infoFecha.hora || '-';

  return (
    <TableRow key={partido.idPartido} hover>
      {columns.map((col) => {
        if (col.field === 'fecha') {
          return (
            <TableCellApp key={col.field} sx={{ py: 0.5, pl: 1.5, pr: 0, textAlign: 'left', width: col.width }}>
              {textoFecha !== '-' ? (
                <Tooltip title={infoFecha.completa || textoFecha} arrow>
                  <TypographyApp
                    sx={{
                      fontSize: '0.74rem',
                      color: 'text.secondary',
                      fontWeight: 600,
                      textAlign: 'left',
                      whiteSpace: 'nowrap',
                      width: '100%',
                    }}
                  >
                    {textoFecha}
                  </TypographyApp>
                </Tooltip>
              ) : (
                <TypographyApp
                  sx={{
                    fontSize: '0.74rem',
                    color: 'text.disabled',
                    textAlign: 'left',
                    whiteSpace: 'nowrap',
                    width: '100%',
                  }}
                >
                  -
                </TypographyApp>
              )}
            </TableCellApp>
          );
        }

        if (col.field === 'hora') {
          return (
            <TableCellApp key={col.field} sx={{ py: 0.5, pl: 0.25, pr: 0.75, textAlign: 'left', width: col.width }}>
              <TypographyApp
                sx={{
                  fontSize: '0.74rem',
                  color: textoHora !== '-' ? '#1e293b' : 'text.disabled',
                  fontWeight: textoHora !== '-' ? 600 : 400,
                  textAlign: 'left',
                  whiteSpace: 'nowrap',
                  width: '100%',
                }}
              >
                {textoHora}
              </TypographyApp>
            </TableCellApp>
          );
        }

        if (col.field === 'localia') {
          return (
            <TableCellApp
              align="center"
              key={col.field}
              sx={{
                py: 0.5,
                pl: 1,
                pr: 0.25,
                textAlign: 'center',
                width: col.width,
                borderLeft: '1px solid #e2e8f0',
              }}
            >
              <IconApp sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {partido.esLocal ? (
                  <Tooltip title="Local" arrow>
                    <Home sx={{ fontSize: '1.05rem', color: 'text.secondary' }} />
                  </Tooltip>
                ) : (
                  <Tooltip title="Visitante" arrow>
                    <Flight sx={{ fontSize: '1.05rem', color: 'text.secondary' }} />
                  </Tooltip>
                )}
              </IconApp>
            </TableCellApp>
          );
        }

        if (col.field === 'jornada') {
          return (
            <TableCellApp key={col.field} sx={{ py: 0.5, pl: 0.25, pr: 0, width: col.width }}>
              <TypographyApp sx={{ fontWeight: 700, fontSize: '0.78rem' }}>J{partido.jornada || '-'}</TypographyApp>
            </TableCellApp>
          );
        }

        if (col.field === 'rival') {
          return (
            <TableCellApp
              key={col.field}
              sx={{ py: 0.5, pl: 0, pr: onEvaluar ? 0.5 : 1.25, overflow: 'hidden', width: col.width }}
            >
              <ButtonTableApp
                onClick={() => {
                  if (idRival) {
                    navigate(`/equipo/${idRival}`);
                  }
                }}
                text={capitalizar(nombreRival)}
                tooltip={capitalizar(nombreRival)}
                sx={{ px: 0.25 }}
              />
            </TableCellApp>
          );
        }

        if (col.field === 'evaluar') {
          return (
            <TableCellApp key={col.field} sx={{ py: 0.5, pl: 0.25, pr: 1.25, textAlign: 'center', width: col.width }}>
              <ChipEstadoEvaluacion estado={partido.estadoEvaluacion} onClick={() => onEvaluar && onEvaluar(partido)} />
            </TableCellApp>
          );
        }

        return <TypographyTableApp key={col.field}>-</TypographyTableApp>;
      })}
    </TableRow>
  );
};

FilaProximoPartido.propTypes = {
  partido: PropTypes.object.isRequired,
  columns: PropTypes.array.isRequired,
  onEvaluar: PropTypes.func,
};

/**
 * Fila individual para la tabla de plantilla de rendimiento de scouting
 */
const FilaRendimientoJugador = ({ jugador, columns, onVerHistorial }) => {
  const navigate = useNavigate();

  const anoTemporada = jugador.temporada ? parseInt(jugador.temporada.split('-')[1], 10) : new Date().getFullYear();
  const categoriaJugador = obtenerCategoriaCodigo(anoTemporada, jugador.ano);
  const categoriaEquipo = jugador.categoria;
  const estadoCategoriaJugador = obtenerEstadoJugadorEnCategoria(categoriaJugador, categoriaEquipo);
  const porcentajeTitular = obtenerPorcentajeTitular(jugador.convocatorias, jugador.titularidades);
  const opacidad = jugador.titularidades >= 5 ? porcentajeTitular : 0;

  const estiloNotaMedia = obtenerColorNota(jugador.notaMediaTemporada);

  return (
    <TableRow key={jugador.idJugador} hover>
      {columns.map((col) => {
        if (col.field === 'nombreJugador') {
          return (
            <TypographyTableApp key={col.field}>
              <ButtonTableApp
                onClick={() => navigate(`/jugador/${jugador.idJugador}`)}
                text={capitalizar(jugador.nombreJugador)}
              />
            </TypographyTableApp>
          );
        }

        if (col.field === 'notaMediaTemporada') {
          const tieneNota = jugador.notaMediaTemporada !== null && jugador.notaMediaTemporada !== undefined;
          return (
            <TableCellApp key={col.field} sx={{ textAlign: 'center', py: 0.5, px: 0.25 }}>
              <Tooltip title="Haz clic para ver historial de partidos y notas" arrow>
                <Chip
                  size="small"
                  label={
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.1 }}>
                      <span>{tieneNota ? jugador.notaMediaTemporada.toFixed(1) : '-'}</span>
                      <ChevronRightRounded sx={{ fontSize: '1.05rem', opacity: 0.95, mr: -0.35, ml: -0.1 }} />
                    </Box>
                  }
                  onClick={onVerHistorial ? () => onVerHistorial(jugador) : undefined}
                  clickable={Boolean(onVerHistorial)}
                  sx={{
                    cursor: onVerHistorial ? 'pointer' : 'default',
                    backgroundColor: estiloNotaMedia.bg,
                    color: estiloNotaMedia.color,
                    border: `1px solid ${estiloNotaMedia.border}`,
                    fontWeight: 700,
                    fontSize: '0.84rem',
                    height: 25,
                    width: 56,
                    minWidth: 56,
                    maxWidth: 56,
                    borderRadius: '5px',
                    transition: 'all 0.15s ease-in-out',
                    '&:hover': onVerHistorial
                      ? {
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.18)',
                          transform: 'translateY(-1px)',
                          filter: 'brightness(0.97)',
                        }
                      : {},
                    '& .MuiChip-label': {
                      px: 0.25,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    },
                  }}
                />
              </Tooltip>
            </TableCellApp>
          );
        }

        if (col.field === 'categoria') {
          return (
            <TableCellCategoria key={col.field} estadoJugador={estadoCategoriaJugador} align="center">
              {categoriaJugador}
            </TableCellCategoria>
          );
        }

        if (col.field === 'porcentajeTitular') {
          return (
            <TableCellTitular key={col.field} opacidad={opacidad}>
              {porcentajeTitular}
            </TableCellTitular>
          );
        }

        return (
          <TypographyTableApp key={col.field}>
            {jugador[col.field] ?? 0}
            {col.field === 'minutos' ? "'" : ''}
          </TypographyTableApp>
        );
      })}
    </TableRow>
  );
};

FilaRendimientoJugador.propTypes = {
  jugador: PropTypes.object.isRequired,
  columns: PropTypes.array.isRequired,
  onVerHistorial: PropTypes.func,
};

/**
 * Componente principal de la pestaña de "Valoración".
 */
const ValoracionScouting = ({ datosEquipo, temporada, idLiga, datosLiga, esEquipoPropio = true }) => {
  const {
    obtenerEstadosEvaluacionEquipo,
    obtenerPlantillaRendimientoScouting,
    obtenerClasificacion,
    equiposCache,
    setEquipoCacheItem,
  } = useContext(Context);

  const cachedTemporada = equiposCache?.[datosEquipo?.idEquipo]?.temporadas?.[idLiga];
  const cachedPartidos = cachedTemporada?.partidos;
  const cachedPlantilla = cachedTemporada?.plantillaRendimiento;
  const cachedClasificacion = cachedTemporada?.clasificacion;

  const [partidos, setPartidos] = useState(cachedPartidos || []);
  const [plantillaRendimiento, setPlantillaRendimiento] = useState(cachedPlantilla || []);
  const [clasificacionData, setClasificacionData] = useState(cachedClasificacion || null);

  const [loadingPartidos, setLoadingPartidos] = useState(!cachedPartidos);
  const [loadingPlantilla, setLoadingPlantilla] = useState(!cachedPlantilla);
  const [loadingClasificacion, setLoadingClasificacion] = useState(!cachedClasificacion);

  const currentKey = `${datosEquipo?.idEquipo}-${temporada}-${idLiga}`;
  const currentKeyRef = useRef(currentKey);
  currentKeyRef.current = currentKey;

  const [partidoSeleccionado, setPartidoSeleccionado] = useState(null);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [jugadorHistorial, setJugadorHistorial] = useState(null);
  const [modalHistorialAbierto, setModalHistorialAbierto] = useState(false);
  const [sort, setSort] = useState({
    field: esEquipoPropio ? 'notaMediaTemporada' : 'minutos',
    direction: 'desc',
  });

  const handleAbrirHistorial = (jugador) => {
    setJugadorHistorial(jugador);
    setModalHistorialAbierto(true);
  };

  const handleCerrarHistorial = () => {
    setModalHistorialAbierto(false);
    setJugadorHistorial(null);
  };

  // Pestañas para dividir partidos a evaluar y partidos futuros
  const [tabPartidos, setTabPartidos] = useState(0); // 0: Evaluar, 1: Próximos

  // Paginación para la tabla compacta de partidos
  const [pagePartidos, setPagePartidos] = useState(0);
  const [rowsPerPagePartidos, setRowsPerPagePartidos] = useState(10);

  // Carga independiente de Partidos
  const recargarPartidos = async (targetIdEquipo, targetTemporada, targetIdLiga, forceSpinner = false) => {
    if (!targetIdEquipo) {
      return;
    }
    if (forceSpinner) {
      setLoadingPartidos(true);
    }
    try {
      const partidosData = await obtenerEstadosEvaluacionEquipo(targetIdEquipo, targetTemporada);
      if (currentKeyRef.current === `${targetIdEquipo}-${targetTemporada}-${targetIdLiga}`) {
        setPartidos(partidosData || []);
        setLoadingPartidos(false);
      }
      setEquipoCacheItem(targetIdEquipo, {
        temporadas: {
          [targetIdLiga]: {
            partidos: partidosData || [],
          },
        },
      });
    } catch (err) {
      console.error('Error al cargar partidos:', err);
      if (currentKeyRef.current === `${targetIdEquipo}-${targetTemporada}-${targetIdLiga}`) {
        setLoadingPartidos(false);
      }
    }
  };

  // Carga independiente de Plantilla Rendimiento
  const recargarPlantilla = async (targetIdEquipo, targetTemporada, targetIdLiga, forceSpinner = false) => {
    if (!targetIdEquipo) {
      return;
    }
    if (forceSpinner) {
      setLoadingPlantilla(true);
    }
    try {
      const plantillaData = await obtenerPlantillaRendimientoScouting(targetIdEquipo, targetIdLiga, targetTemporada);
      if (currentKeyRef.current === `${targetIdEquipo}-${targetTemporada}-${targetIdLiga}`) {
        setPlantillaRendimiento(plantillaData || []);
        setLoadingPlantilla(false);
      }
      setEquipoCacheItem(targetIdEquipo, {
        temporadas: {
          [targetIdLiga]: {
            plantillaRendimiento: plantillaData || [],
          },
        },
      });
    } catch (err) {
      console.error('Error al cargar plantilla:', err);
      if (currentKeyRef.current === `${targetIdEquipo}-${targetTemporada}-${targetIdLiga}`) {
        setLoadingPlantilla(false);
      }
    }
  };

  // Carga independiente de Clasificación
  const recargarClasificacion = async (targetIdEquipo, targetIdLiga, forceSpinner = false) => {
    if (!targetIdLiga) {
      return;
    }
    if (forceSpinner) {
      setLoadingClasificacion(true);
    }
    try {
      const clasifData = await obtenerClasificacion(targetIdLiga);
      if (currentKeyRef.current.endsWith(`-${targetIdLiga}`)) {
        setClasificacionData(clasifData || null);
        setLoadingClasificacion(false);
      }
      if (targetIdEquipo) {
        setEquipoCacheItem(targetIdEquipo, {
          temporadas: {
            [targetIdLiga]: {
              clasificacion: clasifData || [],
            },
          },
        });
      }
    } catch (err) {
      console.error('Error al cargar clasificación:', err);
      if (currentKeyRef.current.endsWith(`-${targetIdLiga}`)) {
        setLoadingClasificacion(false);
      }
    }
  };

  // Efecto principal al cambiar de equipo, liga o temporada
  useEffect(() => {
    if (!datosEquipo?.idEquipo) {
      return;
    }

    const currentCached = equiposCache?.[datosEquipo.idEquipo]?.temporadas?.[idLiga];

    // Partidos
    if (currentCached?.partidos) {
      setPartidos(currentCached.partidos);
      setLoadingPartidos(false);
      recargarPartidos(datosEquipo.idEquipo, temporada, idLiga, false);
    } else {
      setPartidos([]);
      recargarPartidos(datosEquipo.idEquipo, temporada, idLiga, true);
    }

    // Plantilla
    if (currentCached?.plantillaRendimiento) {
      setPlantillaRendimiento(currentCached.plantillaRendimiento);
      setLoadingPlantilla(false);
      recargarPlantilla(datosEquipo.idEquipo, temporada, idLiga, false);
    } else {
      setPlantillaRendimiento([]);
      recargarPlantilla(datosEquipo.idEquipo, temporada, idLiga, true);
    }

    // Clasificación
    if (currentCached?.clasificacion) {
      setClasificacionData(currentCached.clasificacion);
      setLoadingClasificacion(false);
      recargarClasificacion(datosEquipo.idEquipo, idLiga, false);
    } else if (idLiga) {
      setClasificacionData(null);
      recargarClasificacion(datosEquipo.idEquipo, idLiga, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datosEquipo?.idEquipo, idLiga, temporada]);

  // Reiniciar página de partidos y ordenación al cambiar de temporada o equipo
  useEffect(() => {
    setPagePartidos(0);
    setSort({
      field: esEquipoPropio ? 'notaMediaTemporada' : 'minutos',
      direction: 'desc',
    });
  }, [temporada, datosEquipo?.idEquipo, esEquipoPropio]);

  const handleAbrirEvaluacion = (partido) => {
    setPartidoSeleccionado(partido);
    setModalAbierto(true);
  };

  const handleCerrarEvaluacion = () => {
    setModalAbierto(false);
    setPartidoSeleccionado(null);
  };

  const handleGuardadoExitoso = () => {
    recargarPartidos(datosEquipo?.idEquipo, temporada, idLiga, false);
    recargarPlantilla(datosEquipo?.idEquipo, temporada, idLiga, false);
  };

  const plantillaOrdenada = useMemo(() => {
    return ordenarLista(plantillaRendimiento, sort);
  }, [plantillaRendimiento, sort]);

  // Partidos disputados (para evaluar), ordenados descendentemente (el más reciente primero)
  const partidosDisputados = useMemo(() => {
    if (!Array.isArray(partidos)) {
      return [];
    }
    const jugados = partidos.filter((p) => Boolean(p.jugado) || (p.golesLocal !== null && p.golesLocal !== undefined));
    return jugados.sort((a, b) => {
      const timeA = parseFechaTimestamp(a.fecha);
      const timeB = parseFechaTimestamp(b.fecha);
      if (timeA && timeB && timeA !== timeB) {
        return timeB - timeA;
      }
      return (b.jornada || 0) - (a.jornada || 0);
    });
  }, [partidos]);

  // Partidos futuros pendientes de disputar, ordenados ascendentemente (el próximo primero)
  const partidosFuturos = useMemo(() => {
    if (!Array.isArray(partidos)) {
      return [];
    }
    const futuros = partidos.filter((p) => !p.jugado && (p.golesLocal === null || p.golesLocal === undefined));
    return futuros.sort((a, b) => {
      const timeA = parseFechaTimestamp(a.fecha);
      const timeB = parseFechaTimestamp(b.fecha);
      if (timeA && timeB && timeA !== timeB) {
        return timeA - timeB;
      }
      return (a.jornada || 0) - (b.jornada || 0);
    });
  }, [partidos]);

  const esTabEvaluar = tabPartidos === 0;
  const listaPartidosActiva = esTabEvaluar ? partidosDisputados : partidosFuturos;
  const columnasPartidosActivas = !esEquipoPropio
    ? esTabEvaluar
      ? COLUMNAS_ULTIMOS_AJENO
      : COLUMNAS_PROXIMOS_AJENO
    : esTabEvaluar
      ? COLUMNAS_EVALUAR
      : COLUMNAS_PROXIMOS;

  const headerTabsPartidos = (
    <Box sx={{ borderBottom: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
      <Tabs
        value={tabPartidos}
        onChange={(e, nuevoValor) => {
          setTabPartidos(nuevoValor);
          setPagePartidos(0);
        }}
        variant="fullWidth"
        textColor="primary"
        indicatorColor="primary"
        sx={{
          minHeight: 38,
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.82rem',
            minHeight: 38,
            py: 0.5,
            px: 1,
            color: '#64748b',
            '&.Mui-selected': {
              color: 'primary.main',
              fontWeight: 700,
            },
          },
        }}
      >
        <Tab
          icon={<FactCheck sx={{ fontSize: '1.05rem', mr: 0.5 }} />}
          iconPosition="start"
          label={esEquipoPropio ? `Evaluar (${partidosDisputados.length})` : `Últimos (${partidosDisputados.length})`}
        />
        <Tab
          icon={<EventAvailable sx={{ fontSize: '1.05rem', mr: 0.5 }} />}
          iconPosition="start"
          label={`Próximos (${partidosFuturos.length})`}
        />
      </Tabs>
    </Box>
  );

  return (
    <Box sx={{ mt: 1 }}>
      <Grid container spacing={2.5} columns={{ xs: 12, md: 12 }}>
        {/* Columna Izquierda: Partidos y Clasificación de la liga */}
        <Grid item xs={12} md={5}>
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <TableModuleApp
                noHeader
                headerContent={headerTabsPartidos}
                columns={columnasPartidosActivas}
                loading={loadingPartidos}
                data={listaPartidosActiva.slice(
                  pagePartidos * rowsPerPagePartidos,
                  pagePartidos * rowsPerPagePartidos + rowsPerPagePartidos
                )}
                renderRow={(partido) =>
                  esTabEvaluar ? (
                    <FilaEvaluacionPartido
                      key={partido.idPartido}
                      partido={partido}
                      columns={columnasPartidosActivas}
                      onEvaluar={esEquipoPropio ? handleAbrirEvaluacion : undefined}
                    />
                  ) : (
                    <FilaProximoPartido
                      key={partido.idPartido}
                      partido={partido}
                      columns={columnasPartidosActivas}
                      onEvaluar={esEquipoPropio ? handleAbrirEvaluacion : undefined}
                    />
                  )
                }
                hasPagination
                page={pagePartidos}
                setPage={setPagePartidos}
                rowsPerPage={rowsPerPagePartidos}
                setRowsPerPage={setRowsPerPagePartidos}
                rowsPerPageOptions={[5, 10, 20, 50]}
                totalCount={listaPartidosActiva.length}
                emptyMessage={
                  esTabEvaluar
                    ? esEquipoPropio
                      ? 'No hay partidos pendientes de evaluación o disputados en la temporada.'
                      : 'No hay partidos disputados en la temporada.'
                    : 'No hay partidos futuros programados para esta temporada.'
                }
              />
            </Grid>

            {datosLiga && (
              <Grid item xs={12}>
                {loadingClasificacion || !clasificacionData ? (
                  <PaperApp
                    sx={{
                      border: '0.5px solid #0f766e',
                      width: '100%',
                      p: 4,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minHeight: 180,
                    }}
                  >
                    <CircularProgress size={30} sx={{ color: 'primary.main' }} />
                  </PaperApp>
                ) : (
                  <ClasificacionWrapper
                    clasificacion={clasificacionData}
                    idEquipoResaltado={datosEquipo.idEquipo}
                    datosLiga={datosLiga}
                  />
                )}
              </Grid>
            )}
          </Grid>
        </Grid>

        {/* Columna Derecha: Plantilla y Rendimiento Scouting con columnas completas */}
        <Grid item xs={12} md={7}>
          <TableModuleApp
            title="Rendimiento de Plantilla"
            columns={COLUMNAS_RENDIMIENTO}
            sort={sort}
            setSort={setSort}
            loading={loadingPlantilla}
            data={plantillaOrdenada}
            emptyMessage="No hay jugadores registrados en la plantilla."
            renderRow={(jugador) => (
              <FilaRendimientoJugador
                key={jugador.idJugador}
                jugador={jugador}
                columns={COLUMNAS_RENDIMIENTO}
                onVerHistorial={handleAbrirHistorial}
              />
            )}
          />
        </Grid>
      </Grid>

      {/* Modal/Drawer para Evaluar Partido */}
      {partidoSeleccionado && (
        <ModalValorarPartido
          open={modalAbierto}
          onClose={handleCerrarEvaluacion}
          partido={partidoSeleccionado}
          idEquipo={datosEquipo?.idEquipo}
          categoriaEquipo={datosEquipo?.categoria}
          temporada={temporada}
          onGuardadoExitoso={handleGuardadoExitoso}
        />
      )}

      {/* Modal Historial de Rendimiento y Valoraciones del Jugador */}
      {jugadorHistorial && (
        <ModalHistorialRendimiento
          open={modalHistorialAbierto}
          onClose={handleCerrarHistorial}
          jugador={jugadorHistorial}
          temporada={temporada}
        />
      )}
    </Box>
  );
};

ValoracionScouting.propTypes = {
  datosEquipo: PropTypes.object.isRequired,
  temporada: PropTypes.string,
  idLiga: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  datosLiga: PropTypes.object,
  esEquipoPropio: PropTypes.bool,
};

export default ValoracionScouting;
